import DateBadge from '@/components/deal/delivery-date/date-badge';
import DeliveryDateCombobox from '@/components/deal/delivery-date/delivery-date-combobox';
import DealDeliveryPreferenceBadge from '@/components/deal/delivery-preference/deal-delivery-preference-badge';
import DealDeliveryPreferenceCombobox from '@/components/deal/delivery-preference/deal-delivery-preference-combobox';
import { DeliveryQueueCombobox } from '@/components/deal/delivery-queue/delivery-queue-combobox';
import FinanceManagerBadge from '@/components/deal/finance-manager/finance-manager-badge';
import { FinanceManagerCombobox } from '@/components/deal/finance-manager/finance-manager-combobox';
import DealLenderBadge from '@/components/deal/lenders/deal-lender-bagde';
import DealLenderSelectorCombobox from '@/components/deal/lenders/deal-lender-selector-combobox';
import { DealPriorityBadge } from '@/components/deal/priority/deal-priority-badge';
import { DealPriorityCombobox } from '@/components/deal/priority/deal-priority-combobox';
import SalesRepBadge from '@/components/deal/sales-reps/sales-rep-badge';
import { SalesRepCombobox } from '@/components/deal/sales-reps/sales-rep-combobox';
import { DealStageBadge } from '@/components/deal/stage/deal-stage-badge';
import { DealStageSelectorCombobox } from '@/components/deal/stage/deal-stage-selector-combobox';
import { DealStatusBadge } from '@/components/deal/status/deal-status-badge';
import { DealStatusSelectorCombobox } from '@/components/deal/status/deal-status-selector-combobox';
import { RapidReconStepBadge } from '@/components/deal/vehicle/rapid-recon-step/rapid-recon-step-badge';
import { RapidReconStepCombobox } from '@/components/deal/vehicle/rapid-recon-step/rapid-recon-step-combobox';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import type { Deal } from '@/models/registry';
import type { Dispatch, SetStateAction } from 'react';
import type { HotkeyDialogBox } from './use-all-list-and-board-hotkeys';

const HotkeyDialogBoxes = ({
  openDialog,
  setOpenDialog,
  selectedDeal,
}: {
  openDialog: string | null;
  setOpenDialog: Dispatch<SetStateAction<HotkeyDialogBox>>;
  selectedDeal: Deal | null;
}) => {
  if (!selectedDeal || !openDialog) return null;

  console.log('openDialog', openDialog);

  return (
    <Dialog
      open={openDialog != null}
      onOpenChange={() => {
        setOpenDialog(null);
      }}
    >
      <DialogTitle></DialogTitle>
      <DialogContent
        onClickClose={() => {
          setOpenDialog(null);
        }}
      >
        <div>
          <div>
            {selectedDeal?.short_id}:{' '}
            {selectedDeal?.primary_customer?.full_name}
          </div>
          <div>
            {openDialog === 'priority' && (
              <DealPriorityCombobox
                value={selectedDeal?.priority}
                id={selectedDeal?.id}
                afterOnChange={() => {
                  setOpenDialog(null);
                }}
                onSuccess={(_, variables) => {
                  toast({
                    title: `Priority successfully updated`,
                    description: (
                      <DealPriorityBadge value={variables.priority} />
                    ),
                  });
                }}
                onError={(error, variables) => {
                  toast({
                    title: `Error updating priority to ${variables.priority}`,
                    description: error.message,
                  });
                }}
              />
            )}

            {openDialog === 'status' && (
              <DealStatusSelectorCombobox
                id={selectedDeal?.id}
                shortId={selectedDeal?.short_id}
                afterOnChange={() => {
                  setOpenDialog(null);
                }}
                onSuccess={(_, variables) => {
                  toast({
                    title: `Status successfully updated`,
                    description: <DealStatusBadge value={variables.status} />,
                  });
                }}
                onError={(error, variables) => {
                  toast({
                    title: `Error updating status to ${variables.status}`,
                    description: error.message,
                  });
                }}
              />
            )}

            {openDialog === 'contract-status' && (
              <DealStageSelectorCombobox
                id={selectedDeal?.id}
                shortId={selectedDeal?.short_id}
                afterOnChange={() => {
                  setOpenDialog(null);
                }}
                onSuccess={(_, variables) => {
                  const stage = variables.stage;

                  toast({
                    title: `Stage successfully updated`,
                    description: <DealStageBadge value={stage} />,
                  });
                }}
                onError={(error, variables) => {
                  toast({
                    title: `Error updating stage to ${variables.stage}`,
                    description: error.message,
                  });
                }}
              />
            )}

            {openDialog === 'delivery-date' && (
              <div className="flex flex-col w-full justify-center items-center">
                <div className="text-md">Delivery Date</div>
                <DeliveryDateCombobox
                  delivery_date={selectedDeal?.delivery_date}
                  dealId={selectedDeal?.id}
                  afterOnChange={() => {
                    setOpenDialog(null);
                  }}
                  onSuccess={(_, variables) => {
                    toast({
                      title: `Delivery date successfully updated`,
                      description: (
                        <DateBadge date={variables.delivery_date || ''} />
                      ),
                    });
                  }}
                  onError={(error, variables) => {
                    toast({
                      title: `Error updating delivery date to ${variables.delivery_date}`,
                      description: error.message,
                    });
                  }}
                />
              </div>
            )}

            {selectedDeal &&
              selectedDeal.vehicle_listing &&
              selectedDeal.primary_customer?.full_name &&
              openDialog === 'sales-rep-1' && (
                <div className="flex flex-col w-full justify-center items-center">
                  <div>Sales Rep</div>
                  <SalesRepCombobox
                    dealShortId={selectedDeal?.short_id}
                    dealId={selectedDeal?.id}
                    whichSalesRep="primary"
                    afterOnChange={() => {
                      setOpenDialog(null);
                    }}
                    primaryCustomerFullName={
                      selectedDeal?.primary_customer?.full_name
                    }
                    vehicle={selectedDeal?.vehicle_listing}
                    primarySalesRep={selectedDeal?.primary_sales_rep}
                    secondarySalesRep={selectedDeal?.secondary_sales_rep}
                    onSuccess={(_, variables) => {
                      const salesRep = variables.salesReps[variables.position];

                      toast({
                        title: `Sales rep 1 successfully updated`,
                        description: <SalesRepBadge salesRep={salesRep} />,
                      });
                    }}
                    onError={(error) => {
                      toast({
                        title: `Error updating sales rep 1`,
                        description: error.message,
                      });
                    }}
                  />
                </div>
              )}

            {selectedDeal &&
              selectedDeal.vehicle_listing &&
              selectedDeal.primary_customer?.full_name &&
              openDialog === 'sales-rep-2' && (
                <div className="flex flex-col w-full justify-center items-center">
                  <div>Sales Rep 2</div>
                  <SalesRepCombobox
                    dealShortId={selectedDeal?.short_id}
                    dealId={selectedDeal?.id}
                    whichSalesRep="secondary"
                    afterOnChange={() => {
                      setOpenDialog(null);
                    }}
                    primaryCustomerFullName={
                      selectedDeal?.primary_customer.full_name
                    }
                    vehicle={selectedDeal?.vehicle_listing}
                    primarySalesRep={selectedDeal?.primary_sales_rep}
                    secondarySalesRep={selectedDeal?.secondary_sales_rep}
                    onSuccess={(_, variables) => {
                      const salesRep = variables.salesReps[variables.position];

                      toast({
                        title: `Sales rep 2 successfully updated`,
                        description: <SalesRepBadge salesRep={salesRep} />,
                      });
                    }}
                    onError={(error) => {
                      toast({
                        title: `Error updating sales rep 2`,
                        description: error.message,
                      });
                    }}
                  />
                </div>
              )}

            {openDialog === 'finance-manager' && (
              <div className="flex flex-col w-full justify-center items-center">
                <div>Finance Manager</div>
                <FinanceManagerCombobox
                  id={selectedDeal?.id}
                  value={selectedDeal?.finance_manager}
                  afterOnChange={() => {
                    setOpenDialog(null);
                  }}
                  onSuccess={(_, variables) => {
                    toast({
                      title: `Finance manager successfully updated`,
                      description: (
                        <FinanceManagerBadge
                          financeManager={variables.financeManager}
                        />
                      ),
                    });
                  }}
                  onError={(error) => {
                    toast({
                      title: `Error updating finance manager`,
                      description: error.message,
                    });
                  }}
                />
              </div>
            )}

            {selectedDeal &&
              selectedDeal.vehicle_listing &&
              openDialog === 'rapid-recon-step' && (
                <div className="flex flex-col w-full justify-center items-center">
                  <div>Rapid Recon Step</div>
                  <RapidReconStepCombobox
                    id={selectedDeal?.id}
                    shortId={selectedDeal?.short_id}
                    value={selectedDeal?.rapid_recon_step || 'None'}
                    afterOnChange={() => {
                      setOpenDialog(null);
                    }}
                    onSuccess={(_, variables) => {
                      toast({
                        title: `Rapid recon step successfully updated`,
                        description: (
                          <RapidReconStepBadge
                            value={variables.rapidReconStep}
                          />
                        ),
                      });
                    }}
                    onError={(error) => {
                      toast({
                        title: `Error updating rapid recon step`,
                        description: error.message,
                      });
                    }}
                  />
                </div>
              )}

            {openDialog === 'delivery-preference' && (
              <div className="flex flex-col w-full justify-center items-center">
                <div>Delivery Preference</div>
                <DealDeliveryPreferenceCombobox
                  dealId={selectedDeal?.id}
                  afterOnChange={() => {
                    setOpenDialog(null);
                  }}
                  onSuccess={(_, variables) => {
                    toast({
                      title: `Delivery preference successfully updated`,
                      description: (
                        <DealDeliveryPreferenceBadge
                          value={variables.delivery_preference}
                        />
                      ),
                    });
                  }}
                  onError={(error) => {
                    toast({
                      title: `Error updating delivery preference`,
                      description: error.message,
                    });
                  }}
                />
              </div>
            )}

            {selectedDeal &&
              selectedDeal.vehicle_listing &&
              selectedDeal.delivery_date &&
              openDialog === 'delivery-queue' && (
                <div className="flex flex-col w-full justify-center items-center">
                  <div>Delivery Queue</div>
                  <DeliveryQueueCombobox
                    dealId={selectedDeal?.id}
                    pickupDate={selectedDeal?.pickup_date}
                    delivery_date={selectedDeal?.delivery_date}
                    date_added={
                      selectedDeal?.date_added_to_customer_delivery_queue
                    }
                    afterOnChange={() => {
                      setOpenDialog(null);
                    }}
                    onSuccess={() => {
                      toast({
                        title: `Delivery queue successfully updated`,
                      });
                    }}
                    onError={(error) => {
                      toast({
                        title: `Error updating delivery queue`,
                        description: error.message,
                      });
                    }}
                  />
                </div>
              )}

            {openDialog === 'set-lender' && (
              <div className="flex flex-col w-full justify-center items-center">
                <div>Lender</div>
                <DealLenderSelectorCombobox
                  dealId={selectedDeal?.id}
                  shortId={selectedDeal?.short_id}
                  afterOnChange={() => {
                    // console.log('afterOnChange set-lender');
                    setOpenDialog(null);
                  }}
                  onUpdateSuccess={(_, variables) => {
                    // console.log('onSuccess set-lender', variables);
                    toast({
                      title: `Lender successfully updated`,
                      description: (
                        <DealLenderBadge lender={variables.lender} />
                      ),
                    });
                  }}
                  onUpdateError={(error) => {
                    toast({
                      title: `Error updating lender`,
                      description: error.message,
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HotkeyDialogBoxes;
